import type { ImageAddCandidate } from './ImageAddDialog';

export type ImagePresignRequest = {
  fileName: string;
  contentType: string;
  size: number;
};

export type ImagePresignResponse = {
  uploadUrl: string;
  uploadMethod?: 'PUT';
  uploadHeaders?: Record<string, string>;
  fileKey?: string;
  publicUrl?: string;
  form?: Record<string, unknown>;
  contentType?: string;
  bucket?: string;
  fileName?: string;
};

export type ImageFormPayload = Record<string, unknown>;

export type ImageUploadPlanItem = {
  id: string;
  file: File;
  presign: ImagePresignResponse;
  formPayload: ImageFormPayload;
};

export type ImageUploadPlan = {
  items: ImageUploadPlanItem[];
  formPayloads: ImageFormPayload[];
};

export type CreatePresignedUrl = (
  payload: ImagePresignRequest,
  signal?: AbortSignal
) => Promise<ImagePresignResponse>;

export type CreatePresignedUrlsBatch = (
  payloads: ImagePresignRequest[],
  signal?: AbortSignal
) => Promise<ImagePresignResponse[]>;

export type BuildFormPayload = (file: File, presign: ImagePresignResponse) => ImageFormPayload;

const hasContentTypeHeader = (headers: Record<string, string> | undefined): boolean =>
  !!headers && Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');

const isFileCandidate = (
  candidate: ImageAddCandidate
): candidate is Extract<ImageAddCandidate, { type: 'file' }> => candidate.type === 'file';

export const extractUploadFiles = (candidates: ImageAddCandidate[]): File[] =>
  candidates.filter(isFileCandidate).map((c) => c.file);

export const createPresignRequest = (file: File): ImagePresignRequest => ({
  fileName: file.name || 'image',
  contentType: file.type || 'application/octet-stream',
  size: file.size,
});

export const defaultBuildFormPayload: BuildFormPayload = (file, presign) => ({
  ...(presign.form ?? {}),
  fileKey: presign.fileKey,
  publicUrl: presign.publicUrl,
  name: file.name || 'image',
  contentType: file.type || 'application/octet-stream',
  size: file.size,
});

const createUploadId = (file: File, index: number) =>
  `img-upload-${Date.now()}-${index}-${file.name || 'image'}`;

export const buildImageUploadPlan = async (
  files: File[],
  createPresignedUrl: CreatePresignedUrl,
  buildFormPayload: BuildFormPayload = defaultBuildFormPayload,
  signal?: AbortSignal
): Promise<ImageUploadPlan> => {
  const items = await Promise.all(
    files.map(async (file, index) => {
      const presign = await createPresignedUrl(createPresignRequest(file), signal);
      const formPayload = buildFormPayload(file, presign);
      return {
        id: createUploadId(file, index),
        file,
        presign,
        formPayload,
      };
    })
  );

  return {
    items,
    formPayloads: items.map((item) => item.formPayload),
  };
};

export const buildImageUploadPlanBatch = async (
  files: File[],
  createPresignedUrls: CreatePresignedUrlsBatch,
  buildFormPayload: BuildFormPayload = defaultBuildFormPayload,
  signal?: AbortSignal
): Promise<ImageUploadPlan> => {
  const requests = files.map(createPresignRequest);
  const presigns = await createPresignedUrls(requests, signal);
  const items = files.map((file, index) => {
    const presign = presigns[index];
    if (!presign) {
      throw new Error(`Missing presign response at index ${index}`);
    }
    return {
      id: createUploadId(file, index),
      file,
      presign,
      formPayload: buildFormPayload(file, presign),
    };
  });

  return {
    items,
    formPayloads: items.map((item) => item.formPayload),
  };
};

export const putFileToS3 = async (
  file: File,
  presign: ImagePresignResponse,
  signal?: AbortSignal
): Promise<void> => {
  const headers = {
    ...(presign.uploadHeaders ?? {}),
    ...(!hasContentTypeHeader(presign.uploadHeaders) && presign.contentType
      ? { 'Content-Type': presign.contentType }
      : {}),
  };

  const res = await fetch(presign.uploadUrl, {
    method: presign.uploadMethod ?? 'PUT',
    headers,
    body: file,
    signal,
  });

  if (!res.ok) {
    throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
  }
};

export const uploadImagePlanItems = async (
  items: ImageUploadPlanItem[],
  signal?: AbortSignal
): Promise<void> => {
  await Promise.all(items.map((item) => putFileToS3(item.file, item.presign, signal)));
};
